import { createSlice } from '@reduxjs/toolkit';

import {
  addMediaStudioColor,
  deleteMediaStudioColor,
  deleteMediaStudioFont,
  deleteMediaStudioImage,
  getMediaStudioColors,
  getMediaStudioFonts,
  getMediaStudioImages,
  loadMediaStudioFontFile,
  uploadMediaStudioFont,
  uploadMediaStudioImage,
} from './mediaStudioSettingsActions';

const initialState = {
  images: [],
  isImagesLoading: false,
  uploadingImages: [],
  deletingImageIds: [],

  fonts: [],
  isFontsLoading: false,
  uploadingFonts: [],
  deletingFontIds: [],

  fontFamilies: {},
  loadingFontFileIds: [],
  failedFontFileIds: [],

  colors: [],
  isColorsLoading: false,
  isColorAdding: false,
  deletingColorIds: [],
};

const withoutId = (ids, id) => ids.filter((item) => item !== id);
const withoutItem = (items, id) => items.filter((item) => item.id !== id);

const mediaStudioSettings = createSlice({
  name: 'mediaStudioSettings',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getMediaStudioImages.pending, (state) => {
        state.isImagesLoading = true;
      })
      .addCase(getMediaStudioImages.fulfilled, (state, action) => {
        state.images = action.payload;
        state.isImagesLoading = false;
      })
      .addCase(getMediaStudioImages.rejected, (state) => {
        state.isImagesLoading = false;
      })

      .addCase(uploadMediaStudioImage.pending, (state, action) => {
        state.uploadingImages.push({ id: action.meta.requestId, label: action.meta.arg.name });
      })
      .addCase(uploadMediaStudioImage.fulfilled, (state, action) => {
        state.uploadingImages = withoutItem(state.uploadingImages, action.meta.requestId);
        state.images = action.payload;
      })
      .addCase(uploadMediaStudioImage.rejected, (state, action) => {
        state.uploadingImages = withoutItem(state.uploadingImages, action.meta.requestId);
      })

      .addCase(deleteMediaStudioImage.pending, (state, action) => {
        state.deletingImageIds.push(action.meta.arg);
      })
      .addCase(deleteMediaStudioImage.fulfilled, (state, action) => {
        state.deletingImageIds = withoutId(state.deletingImageIds, action.meta.arg);
        state.images = withoutItem(state.images, action.meta.arg);
      })
      .addCase(deleteMediaStudioImage.rejected, (state, action) => {
        state.deletingImageIds = withoutId(state.deletingImageIds, action.meta.arg);
      })

      .addCase(getMediaStudioFonts.pending, (state) => {
        state.isFontsLoading = true;
      })
      .addCase(getMediaStudioFonts.fulfilled, (state, action) => {
        state.fonts = action.payload;
        state.isFontsLoading = false;
      })
      .addCase(getMediaStudioFonts.rejected, (state) => {
        state.isFontsLoading = false;
      })

      .addCase(uploadMediaStudioFont.pending, (state, action) => {
        state.uploadingFonts.push({ id: action.meta.requestId, label: action.meta.arg.label });
      })
      .addCase(uploadMediaStudioFont.fulfilled, (state, action) => {
        state.uploadingFonts = withoutItem(state.uploadingFonts, action.meta.requestId);
        state.fonts = action.payload;
      })
      .addCase(uploadMediaStudioFont.rejected, (state, action) => {
        state.uploadingFonts = withoutItem(state.uploadingFonts, action.meta.requestId);
      })

      .addCase(deleteMediaStudioFont.pending, (state, action) => {
        state.deletingFontIds.push(action.meta.arg);
      })
      .addCase(deleteMediaStudioFont.fulfilled, (state, action) => {
        const id = action.meta.arg;
        state.deletingFontIds = withoutId(state.deletingFontIds, id);
        state.fonts = withoutItem(state.fonts, id);
        delete state.fontFamilies[id];
      })
      .addCase(deleteMediaStudioFont.rejected, (state, action) => {
        state.deletingFontIds = withoutId(state.deletingFontIds, action.meta.arg);
      })

      .addCase(loadMediaStudioFontFile.pending, (state, action) => {
        state.loadingFontFileIds.push(action.meta.arg);
      })
      .addCase(loadMediaStudioFontFile.fulfilled, (state, action) => {
        state.loadingFontFileIds = withoutId(state.loadingFontFileIds, action.meta.arg);
        state.fontFamilies[action.payload.id] = action.payload.family;
      })
      .addCase(loadMediaStudioFontFile.rejected, (state, action) => {
        state.loadingFontFileIds = withoutId(state.loadingFontFileIds, action.meta.arg);
        state.failedFontFileIds.push(action.meta.arg);
      })

      .addCase(getMediaStudioColors.pending, (state) => {
        state.isColorsLoading = true;
      })
      .addCase(getMediaStudioColors.fulfilled, (state, action) => {
        state.colors = action.payload;
        state.isColorsLoading = false;
      })
      .addCase(getMediaStudioColors.rejected, (state) => {
        state.isColorsLoading = false;
      })

      .addCase(addMediaStudioColor.pending, (state) => {
        state.isColorAdding = true;
      })
      .addCase(addMediaStudioColor.fulfilled, (state, action) => {
        state.colors = action.payload;
        state.isColorAdding = false;
      })
      .addCase(addMediaStudioColor.rejected, (state) => {
        state.isColorAdding = false;
      })

      .addCase(deleteMediaStudioColor.pending, (state, action) => {
        state.deletingColorIds.push(action.meta.arg);
      })
      .addCase(deleteMediaStudioColor.fulfilled, (state, action) => {
        state.deletingColorIds = withoutId(state.deletingColorIds, action.meta.arg);
        state.colors = withoutItem(state.colors, action.meta.arg);
      })
      .addCase(deleteMediaStudioColor.rejected, (state, action) => {
        state.deletingColorIds = withoutId(state.deletingColorIds, action.meta.arg);
      });
  },
});

export default mediaStudioSettings.reducer;
